import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit, Trash2, Eye, RotateCcw, Printer, Search, Upload, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

export default function PurchaseOrders() {
  const [pos, setPOs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const user = getUser();
  const printRef = useRef();

  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const canCreate = userRoles.some(r => ['Admin', 'Purchasing'].includes(r));
  const canUpdatePayment = userRoles.some(r => ['Admin', 'Finance'].includes(r));

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleImportSubmit = async () => {
    if (!importFile) {
      toast.error('Pilih file Excel dulu');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await api.post('/purchase-orders/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      toast.success(res.data.message || 'Import selesai');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import gagal');
    } finally {
      setImporting(false);
    }
  };

  const downloadImportTemplate = () => {
    // Minimal CSV template for users
    const csv = 'PO Number,Date,Supplier,Item Name,Unit,Qty,Price,Remark\n' +
                ',10/03/2026,Supplier ABC,Item Name 1,pcs,10,100000,Contoh remark\n' +
                ',10/03/2026,Supplier ABC,Item Name 2,pcs,5,50000,\n' +
                'PO-CUSTOM-001,11/03/2026,Supplier XYZ,Item Name 3,box,2,200000,\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'PO_Import_Template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Filtered POs based on search
  const filteredPOs = pos.filter(po => 
    po.po_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    po.supplier_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (po.document || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (po.remark || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    po.items.some(item => item.item_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [posRes, suppliersRes, itemsRes] = await Promise.all([
        api.get('/purchase-orders'),
        api.get('/suppliers'),
        api.get('/items')
      ]);
      setPOs(posRes.data);
      setSuppliers(suppliersRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (po) => {
    setSelectedPO(po);
    setViewDialogOpen(true);
  };

  const handleEdit = (po) => {
    setSelectedPO(po);
    setEditDialogOpen(true);
  };

  const handlePrint = (po) => {
    setSelectedPO(po);
    setPrintDialogOpen(true);
  };

  const handleDelete = async (poNumber) => {
    if (!window.confirm('Are you sure you want to delete this PO?')) return;
    
    try {
      await api.delete(`/purchase-orders/${poNumber}`);
      toast.success('PO deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete PO');
    }
  };

  const handleUpdatePayment = async (poNumber, paymentStatus) => {
    try {
      await api.patch(`/purchase-orders/${poNumber}/payment?payment_status=${paymentStatus}`);
      toast.success('Payment status updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    }
  };

  const handleReopen = async (poNumber) => {
    if (!window.confirm('Reopen this PO? Status will be changed to Open and Payment to Unpaid.')) return;
    
    try {
      await api.patch(`/purchase-orders/${poNumber}/reopen`);
      toast.success('PO reopened successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen PO');
    }
  };

  const executePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Print PO - ${selectedPO?.po_number}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h1 { font-size: 18px; margin: 0; }
            .header h2 { font-size: 14px; margin: 5px 0; font-weight: normal; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th, td { border: 1px solid #333; padding: 8px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .total-section { margin: 15px 0; }
            .total-row { display: flex; justify-content: flex-end; margin: 5px 0; }
            .total-label { width: 150px; text-align: right; padding-right: 10px; }
            .total-value { width: 150px; text-align: right; font-weight: bold; }
            .grand-total { font-size: 14px; font-weight: bold; border-top: 2px solid #333; padding-top: 5px; }
            .remark-section { margin: 15px 0; padding: 10px; border: 1px solid #ddd; }
            .signature-section { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; margin-top: 40px; text-align: center; }
            .signature-box { border: 1px solid #333; padding: 15px; min-height: 100px; }
            .signature-title { font-weight: bold; margin-bottom: 60px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-primary mb-2">Purchase Orders</h1>
          <p className="text-muted-foreground">Manage purchase orders</p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { setImportDialogOpen(true); setImportFile(null); setImportResult(null); }}
              data-testid="import-po-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Import Excel
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="create-po-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Create PO
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Purchase Order</DialogTitle>
                </DialogHeader>
                <POForm 
                  suppliers={suppliers} 
                  items={items} 
                  onSuccess={() => {
                    setDialogOpen(false);
                    fetchData();
                  }} 
                />
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Search Input */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search PO number, reference, supplier, item, or remark..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white border-2 border-primary/20"
        />
      </div>

      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="po-table">
            <thead>
              <tr className="border-b bg-secondary/50">
                <th className="text-left p-3 font-semibold">PO Number</th>
                <th className="text-left p-3 font-semibold">Date</th>
                <th className="text-left p-3 font-semibold">Reference</th>
                <th className="text-left p-3 font-semibold">Supplier</th>
                <th className="text-right p-3 font-semibold">Grand Total</th>
                <th className="text-left p-3 font-semibold">Payment</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-center p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPOs.map((po) => (
                <tr key={po.po_number} className="border-b hover:bg-accent/20 transition-colors">
                  <td className="p-3 font-medium">{po.po_number}</td>
                  <td className="p-3">{new Date(po.date).toLocaleDateString('id-ID')}</td>
                  <td className="p-3 text-sm text-muted-foreground" data-testid={`po-reference-${po.po_number}`}>{po.document || '-'}</td>
                  <td className="p-3">{po.supplier_name}</td>
                  <td className="p-3 text-right tabular-nums font-medium">{formatRupiah(po.grand_total || po.total_amount)}</td>
                  <td className="p-3">
                    {canUpdatePayment && po.status === 'Open' ? (
                      <Select
                        value={po.payment_status}
                        onValueChange={(value) => handleUpdatePayment(po.po_number, value)}
                      >
                        <SelectTrigger className="w-36 bg-white border-primary/20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Unpaid">Unpaid</SelectItem>
                          <SelectItem value="DP">DP (Down Payment)</SelectItem>
                          <SelectItem value="Termin">Termin</SelectItem>
                          <SelectItem value="Paid">Paid (Lunas)</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`status-badge status-${po.payment_status.toLowerCase()}`}>
                        {po.payment_status}
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`status-badge status-${po.status.toLowerCase()}`}>
                      {po.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-center space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleView(po)}
                        data-testid={`view-po-${po.po_number}`}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handlePrint(po)}
                        data-testid={`print-po-${po.po_number}`}
                        title="Print PO"
                      >
                        <Printer className="h-4 w-4 text-blue-600" />
                      </Button>
                      {po.status === 'Open' && canCreate && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(po)}
                            data-testid={`edit-po-${po.po_number}`}
                            title="Edit PO"
                          >
                            <Edit className="h-4 w-4 text-primary" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(po.po_number)}
                            data-testid={`delete-po-${po.po_number}`}
                            title="Delete PO"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      )}
                      {po.status === 'Release' && userRoles.includes('Admin') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleReopen(po.po_number)}
                          data-testid={`reopen-po-${po.po_number}`}
                          title="Reopen PO (Admin Only)"
                        >
                          <RotateCcw className="h-4 w-4 text-blue-600" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Purchase Order - {selectedPO?.po_number}</DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <POForm 
              suppliers={suppliers} 
              items={items}
              editMode={true}
              existingPO={selectedPO}
              onSuccess={() => {
                setEditDialogOpen(false);
                fetchData();
              }} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Purchase Order Details</DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>PO Number</Label>
                  <p className="font-medium">{selectedPO.po_number}</p>
                </div>
                <div>
                  <Label>Date</Label>
                  <p className="font-medium">{new Date(selectedPO.date).toLocaleDateString('id-ID')}</p>
                </div>
                <div>
                  <Label>Supplier</Label>
                  <p className="font-medium">{selectedPO.supplier_name}</p>
                </div>
                <div>
                  <Label>Document</Label>
                  <p className="font-medium">{selectedPO.document}</p>
                </div>
                <div>
                  <Label>Created By</Label>
                  <p className="font-medium">{selectedPO.created_by}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <p className="font-medium">{selectedPO.status} / {selectedPO.payment_status}</p>
                </div>
              </div>
              {selectedPO.remark && (
                <div>
                  <Label>Remark/Keterangan</Label>
                  <p className="font-medium bg-secondary/30 p-2 rounded">{selectedPO.remark}</p>
                </div>
              )}
              <div>
                <Label className="mb-2 block">Items</Label>
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-secondary/50">
                      <th className="text-left p-2">Item</th>
                      <th className="text-center p-2">Qty</th>
                      <th className="text-right p-2">Price</th>
                      <th className="text-right p-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPO.items.map((item, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="p-2">{item.item_name}</td>
                        <td className="p-2 text-center">{item.quantity} {item.unit}</td>
                        <td className="p-2 text-right tabular-nums">{formatRupiah(item.price)}</td>
                        <td className="p-2 text-right tabular-nums">{formatRupiah(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-b">
                      <td colSpan="3" className="p-2 text-right">Subtotal:</td>
                      <td className="p-2 text-right tabular-nums">{formatRupiah(selectedPO.total_amount)}</td>
                    </tr>
                    {selectedPO.discount > 0 && (
                      <tr className="border-b">
                        <td colSpan="3" className="p-2 text-right">Discount:</td>
                        <td className="p-2 text-right tabular-nums text-red-600">- {formatRupiah(selectedPO.discount)}</td>
                      </tr>
                    )}
                    {selectedPO.include_ppn !== false && (
                      <tr className="border-b">
                        <td colSpan="3" className="p-2 text-right">PPN 11%:</td>
                        <td className="p-2 text-right tabular-nums">{formatRupiah(selectedPO.ppn)}</td>
                      </tr>
                    )}
                    <tr className="font-bold bg-secondary/30">
                      <td colSpan="3" className="p-2 text-right">Grand Total:</td>
                      <td className="p-2 text-right tabular-nums">{formatRupiah(selectedPO.grand_total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="flex justify-end pt-4">
                <Button onClick={() => handlePrint(selectedPO)} className="btn-primary">
                  <Printer className="h-4 w-4 mr-2" />
                  Print PO
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Print Preview - {selectedPO?.po_number}</DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <>
              <div ref={printRef} className="bg-white p-6 border rounded">
                {/* Header */}
                <div className="header text-center mb-6">
                  <h1 className="text-xl font-bold">ROYAL TULIP BATU</h1>
                  <h2 className="text-lg font-semibold mt-2">PURCHASE ORDER</h2>
                  <p className="text-sm text-gray-600">{selectedPO.po_number}</p>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="w-28 font-semibold">No. PO</span>
                      <span>: {selectedPO.po_number}</span>
                    </div>
                    <div className="flex">
                      <span className="w-28 font-semibold">Tanggal</span>
                      <span>: {new Date(selectedPO.date).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                    <div className="flex">
                      <span className="w-28 font-semibold">Document</span>
                      <span>: {selectedPO.document}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex">
                      <span className="w-28 font-semibold">Supplier</span>
                      <span>: {selectedPO.supplier_name}</span>
                    </div>
                    <div className="flex">
                      <span className="w-28 font-semibold">Status</span>
                      <span>: {selectedPO.status}</span>
                    </div>
                    <div className="flex">
                      <span className="w-28 font-semibold">Payment</span>
                      <span>: {selectedPO.payment_status}</span>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <table className="w-full border-collapse mb-4">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 p-2 text-center w-12">No</th>
                      <th className="border border-gray-300 p-2 text-left">Barang</th>
                      <th className="border border-gray-300 p-2 text-center w-20">Qty</th>
                      <th className="border border-gray-300 p-2 text-center w-20">Satuan</th>
                      <th className="border border-gray-300 p-2 text-right w-32">Harga</th>
                      <th className="border border-gray-300 p-2 text-right w-36">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPO.items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="border border-gray-300 p-2 text-center">{idx + 1}</td>
                        <td className="border border-gray-300 p-2">{item.item_name}</td>
                        <td className="border border-gray-300 p-2 text-center">{item.quantity}</td>
                        <td className="border border-gray-300 p-2 text-center">{item.unit}</td>
                        <td className="border border-gray-300 p-2 text-right">{formatRupiah(item.price)}</td>
                        <td className="border border-gray-300 p-2 text-right">{formatRupiah(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Total Section */}
                <div className="flex justify-end mb-4">
                  <div className="w-72">
                    <div className="flex justify-between py-1 border-b">
                      <span>Subtotal</span>
                      <span className="font-medium">{formatRupiah(selectedPO.total_amount)}</span>
                    </div>
                    {(selectedPO.discount > 0) && (
                      <div className="flex justify-between py-1 border-b text-red-600">
                        <span>Discount</span>
                        <span className="font-medium">- {formatRupiah(selectedPO.discount)}</span>
                      </div>
                    )}
                    {selectedPO.include_ppn !== false && (
                      <div className="flex justify-between py-1 border-b">
                        <span>PPN 11%</span>
                        <span className="font-medium">{formatRupiah(selectedPO.ppn)}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-t-2 border-gray-800 font-bold text-lg">
                      <span>GRAND TOTAL</span>
                      <span>{formatRupiah(selectedPO.grand_total)}</span>
                    </div>
                  </div>
                </div>

                {/* Remark Section */}
                <div className="mb-6 p-3 border border-gray-300 rounded">
                  <p className="font-semibold mb-1">Keterangan:</p>
                  <p className="text-sm">{selectedPO.remark || '-'}</p>
                </div>

                {/* Signature Section - Kosong */}
                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="border border-gray-300 p-4 text-center">
                    <p className="font-semibold mb-16">Dibuat Oleh</p>
                    <div className="border-t border-gray-400 pt-2 mt-2">
                      <p className="font-medium">(________________)</p>
                    </div>
                  </div>
                  <div className="border border-gray-300 p-4 text-center">
                    <p className="font-semibold mb-16">Disetujui</p>
                    <div className="border-t border-gray-400 pt-2 mt-2">
                      <p className="font-medium">(________________)</p>
                    </div>
                  </div>
                  <div className="border border-gray-300 p-4 text-center">
                    <p className="font-semibold mb-16">Disetujui 2</p>
                    <div className="border-t border-gray-400 pt-2 mt-2">
                      <p className="font-medium">(________________)</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setPrintDialogOpen(false)}>
                  Close
                </Button>
                <Button onClick={executePrint} className="btn-primary">
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Excel Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Purchase Orders dari Excel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground space-y-2">
              <p>Format kolom Excel (urutan & nama kolom):</p>
              <code className="block bg-secondary/50 p-2 rounded text-xs">
                PO Number | Date (DD/MM/YYYY) | Supplier | Item Name | Unit | Qty | Price | Remark
              </code>
              <ul className="list-disc list-inside text-xs space-y-1">
                <li><b>PO Number</b> kosong = auto-generate (1 PO per kombinasi Supplier+Tanggal)</li>
                <li><b>Supplier</b> & <b>Item</b> baru akan <b>otomatis dibuat</b> di Master Data (kategori: Uncategorized)</li>
                <li>Untuk hindari duplikat, tulis nama <b>persis sama</b> dengan yang sudah ada di Master (case-insensitive)</li>
                <li>PPN 11% otomatis ditambahkan</li>
              </ul>
            </div>

            <Button variant="outline" size="sm" onClick={downloadImportTemplate} data-testid="download-po-template-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Download Template CSV
            </Button>

            <div>
              <Label htmlFor="po-import-file">File Excel (.xlsx)</Label>
              <Input
                id="po-import-file"
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="po-import-file-input"
              />
            </div>

            {importResult && (
              <div className="rounded-lg border p-3 space-y-2 text-sm">
                <p className="font-medium text-green-700">
                  ✅ {importResult.created_count} PO berhasil dibuat
                  {importResult.skipped_count > 0 && (
                    <span className="text-orange-600"> • {importResult.skipped_count} baris dilewati</span>
                  )}
                </p>
                {importResult.created_po_numbers?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold mb-1">PO yang dibuat:</p>
                    <p className="text-xs font-mono break-all">{importResult.created_po_numbers.join(', ')}</p>
                  </div>
                )}
                {importResult.auto_created_suppliers?.length > 0 && (
                  <div className="bg-blue-50 p-2 rounded">
                    <p className="text-xs font-semibold mb-1 text-blue-800">
                      ➕ {importResult.auto_created_suppliers.length} supplier auto-dibuat di Master:
                    </p>
                    <p className="text-xs">{importResult.auto_created_suppliers.join(', ')}</p>
                  </div>
                )}
                {importResult.auto_created_items?.length > 0 && (
                  <div className="bg-blue-50 p-2 rounded max-h-32 overflow-y-auto">
                    <p className="text-xs font-semibold mb-1 text-blue-800">
                      ➕ {importResult.auto_created_items.length} item auto-dibuat di Master (kategori: Uncategorized):
                    </p>
                    <p className="text-xs">{importResult.auto_created_items.join(', ')}</p>
                  </div>
                )}
                {importResult.skipped_rows?.length > 0 && (
                  <div className="bg-orange-50 p-2 rounded max-h-40 overflow-y-auto">
                    <p className="text-xs font-semibold mb-1 text-orange-800">Baris dilewati:</p>
                    <ul className="text-xs space-y-0.5">
                      {importResult.skipped_rows.map((s, i) => (
                        <li key={i}>Baris {s.row}: {s.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Tutup</Button>
              <Button
                onClick={handleImportSubmit}
                disabled={!importFile || importing}
                className="btn-primary"
                data-testid="po-import-submit-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function POForm({ suppliers, items, onSuccess, editMode = false, existingPO = null }) {
  const [formData, setFormData] = useState(
    editMode && existingPO ? {
      document: existingPO.document,
      date: new Date(existingPO.date).toISOString().split('T')[0],
      supplier_id: existingPO.supplier_id,
      payment_status: existingPO.payment_status,
      remark: existingPO.remark || '',
      discount: existingPO.discount || 0,
      include_ppn: existingPO.include_ppn !== false,
      items: existingPO.items
    } : {
      document: '',
      date: new Date().toISOString().split('T')[0],
      supplier_id: '',
      payment_status: 'Unpaid',
      remark: '',
      discount: 0,
      include_ppn: true,
      items: []
    }
  );
  
  const [currentItem, setCurrentItem] = useState({
    item_id: '',
    quantity: '',
    price: ''
  });

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const addItem = () => {
    if (!currentItem.item_id || !currentItem.quantity || !currentItem.price) {
      toast.error('Please fill all item fields (Item, Quantity, and Price)');
      return;
    }

    const item = items.find(i => i.id === currentItem.item_id);
    const quantity = parseFloat(currentItem.quantity);
    const price = parseFloat(currentItem.price);
    
    const newItem = {
      item_id: item.id,
      item_name: item.name,
      unit: item.unit,
      quantity: quantity,
      price: price,
      amount: quantity * price
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    setCurrentItem({ item_id: '', quantity: '', price: '' });
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + item.amount, 0);
  const discount = parseFloat(formData.discount) || 0;
  const afterDiscount = subtotal - discount;
  const ppn = formData.include_ppn ? afterDiscount * 0.11 : 0;
  const grandTotal = afterDiscount + ppn;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      const submitData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        remark: formData.remark || null,
        discount: parseFloat(formData.discount) || 0,
        include_ppn: formData.include_ppn
      };

      if (editMode) {
        await api.put(`/purchase-orders/${existingPO.po_number}`, submitData);
        toast.success('PO updated successfully');
      } else {
        await api.post('/purchase-orders', submitData);
        toast.success('PO created successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'create'} PO`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Document Reference *</Label>
          <Input
            value={formData.document}
            onChange={(e) => setFormData({ ...formData, document: e.target.value })}
            required
            placeholder="e.g., INV-001"
            className="bg-white border-2 border-primary/30 focus:border-primary"
          />
        </div>
        <div>
          <Label>Date *</Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            className="bg-white border-2 border-primary/30 focus:border-primary"
          />
        </div>
      </div>

      <div>
        <Label>Supplier *</Label>
        <Select value={formData.supplier_id} onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}>
          <SelectTrigger className="bg-white border-2 border-primary/30">
            <SelectValue placeholder="Select supplier" />
          </SelectTrigger>
          <SelectContent>
            {suppliers.map((supplier) => (
              <SelectItem key={supplier.id} value={supplier.id}>
                {supplier.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Remark Field */}
      <div>
        <Label>Remark / Keterangan</Label>
        <Textarea
          value={formData.remark}
          onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
          placeholder="Tambahkan keterangan atau catatan untuk PO ini..."
          className="bg-white border-2 border-primary/30 focus:border-primary min-h-[80px]"
        />
      </div>

      <div className="border-t-2 border-primary/20 pt-6">
        <Label className="text-lg font-semibold mb-4 block">Add Items</Label>
        
        <div className="bg-accent/20 p-4 rounded-lg mb-4">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-5">
              <Label className="text-sm">Item *</Label>
              <Select value={currentItem.item_id} onValueChange={(value) => setCurrentItem({ ...currentItem, item_id: value })}>
                <SelectTrigger className="bg-white border-2 border-primary/30">
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {items.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-sm">Quantity *</Label>
              <Input
                type="number"
                placeholder="Qty"
                value={currentItem.quantity}
                onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                step="0.01"
                min="0"
                className="bg-white border-2 border-primary/30"
              />
            </div>
            <div className="col-span-3">
              <Label className="text-sm">Price (Rp) *</Label>
              <Input
                type="number"
                placeholder="Price per unit"
                value={currentItem.price}
                onChange={(e) => setCurrentItem({ ...currentItem, price: e.target.value })}
                step="100"
                min="0"
                className="bg-white border-2 border-primary/30"
              />
            </div>
            <div className="col-span-2 flex items-end">
              <Button type="button" onClick={addItem} className="w-full btn-primary">
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            * Pilih item, isi quantity dan price per unit, lalu klik Add
          </p>
        </div>

        {formData.items.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-primary/10 border-b">
                  <th className="text-left p-3 font-semibold">Item</th>
                  <th className="text-center p-3 font-semibold">Qty</th>
                  <th className="text-right p-3 font-semibold">Price</th>
                  <th className="text-right p-3 font-semibold">Amount</th>
                  <th className="text-center p-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {formData.items.map((item, idx) => (
                  <tr key={idx} className="border-b hover:bg-accent/10">
                    <td className="p-3">{item.item_name}</td>
                    <td className="p-3 text-center">{item.quantity} {item.unit}</td>
                    <td className="p-3 text-right tabular-nums">{formatRupiah(item.price)}</td>
                    <td className="p-3 text-right tabular-nums font-medium">{formatRupiah(item.amount)}</td>
                    <td className="p-3 text-center">
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Discount and Total Section */}
      {formData.items.length > 0 && (
        <div className="bg-secondary/30 p-4 rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <Label>Discount (Rp)</Label>
                <Input
                  type="number"
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                  placeholder="0"
                  step="1000"
                  min="0"
                  className="bg-white border-2 border-primary/30"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="include_ppn"
                  checked={formData.include_ppn}
                  onChange={(e) => setFormData({ ...formData, include_ppn: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <Label htmlFor="include_ppn" className="cursor-pointer">
                  Include PPN 11%
                </Label>
              </div>
            </div>
            <div className="space-y-2 text-right">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-medium">{formatRupiah(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Discount:</span>
                  <span className="font-medium">- {formatRupiah(discount)}</span>
                </div>
              )}
              {formData.include_ppn && (
                <div className="flex justify-between">
                  <span>PPN 11%:</span>
                  <span className="font-medium">{formatRupiah(ppn)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Grand Total:</span>
                <span>{formatRupiah(grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <Button type="submit" className="w-full btn-primary text-lg py-6">
        {editMode ? 'Update Purchase Order' : 'Create Purchase Order'}
      </Button>
    </form>
  );
}
